import { useSyncExternalStore } from 'react'
import * as storage from '@/lib/safeStorage'

// Artists default to a personal schedule (same as crew) but can toggle to
// "show all" to peek at activities they aren't assigned to — useful for
// coordinating arrival times around load-in, soundcheck, etc.
//
// Stored in localStorage so the choice persists across sessions and reloads.
//
// Module-level store + useSyncExternalStore so every caller (the header
// toggle AND the page that filters activities) reads the same value. A
// plain useState per call would give each component its own copy and the
// toggle's setState wouldn't reach the pages.

const STORAGE_KEY = 'tourflow-artist-view-mode'
export type ArtistViewMode = 'mine' | 'all'

function read(): ArtistViewMode {
  const stored = storage.getItem(STORAGE_KEY)
  return stored === 'all' ? 'all' : 'mine'
}

let current: ArtistViewMode = read()
const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function getSnapshot(): ArtistViewMode {
  return current
}

function setMode(next: ArtistViewMode) {
  if (current === next) return
  current = next
  storage.setItem(STORAGE_KEY, next)
  listeners.forEach(l => l())
}

export function useArtistViewMode() {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  function toggle() {
    setMode(current === 'mine' ? 'all' : 'mine')
  }

  return { mode, setMode, toggle }
}
